import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  fsGetWeight,
  fsLogWeight,
  fsGetWeekWeight,
  fsGetWeightPrefs,
  fsSaveWeightPrefs,
} from '../services/firestoreDb';

function getToday() {
  return new Date().toISOString().split('T')[0];
}

function localGetWeight() {
  const val = localStorage.getItem(`sm_weight_${getToday()}`);
  return val ? parseFloat(val) : null;
}

function localSetWeight(w) {
  localStorage.setItem(`sm_weight_${getToday()}`, String(w));
}

function localGetUnit() {
  return localStorage.getItem('sm_weight_unit') || 'kg';
}

function localSetUnit(u) {
  localStorage.setItem('sm_weight_unit', u);
}

function localGetHeight() {
  return parseFloat(localStorage.getItem('sm_height') || '170');
}

function localSetHeight(h) {
  localStorage.setItem('sm_height', String(h));
}

function localGetWeekData() {
  const now = new Date();
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(now);
    d.setDate(d.getDate() - (6 - i));
    const date = d.toISOString().split('T')[0];
    const day = d.toLocaleDateString('en-GB', { weekday: 'short' });
    const val = localStorage.getItem(`sm_weight_${date}`);
    return { date, day, weight: val ? parseFloat(val) : null };
  });
}

export function useWeight() {
  const { user } = useAuth();
  const uid = user?.id;

  const [todayWeight, setTodayWeightState] = useState(() => localGetWeight());
  const [unit, setUnitState] = useState(() => localGetUnit());
  const [height, setHeightState] = useState(() => localGetHeight());
  const [weekData, setWeekData] = useState(() => localGetWeekData());

  useEffect(() => {
    if (!uid) return;
    Promise.all([fsGetWeight(uid), fsGetWeekWeight(uid), fsGetWeightPrefs(uid)])
      .then(([today, week, prefs]) => {
        if (today !== null) {
          if (today.weight != null) setTodayWeightState(today.weight);
          if (today.unit != null) setUnitState(today.unit);
        }
        if (week !== null) setWeekData(week);
        if (prefs !== null) {
          if (prefs.height != null) setHeightState(prefs.height);
          if (prefs.weightUnit != null) setUnitState(prefs.weightUnit);
        }
      })
      .catch(() => {});
  }, [uid]);

  const logWeight = useCallback(
    (w) => {
      const val = parseFloat(w);
      localSetWeight(val);
      setTodayWeightState(val);
      setWeekData(localGetWeekData());
      if (uid) fsLogWeight(uid, val, unit).catch(() => {});
    },
    [uid, unit]
  );

  const setUnit = useCallback(
    (u) => {
      localSetUnit(u);
      setUnitState(u);
      if (uid) fsSaveWeightPrefs(uid, { weightUnit: u }).catch(() => {});
    },
    [uid]
  );

  const setHeight = useCallback(
    (h) => {
      const val = parseFloat(h);
      localSetHeight(val);
      setHeightState(val);
      if (uid) fsSaveWeightPrefs(uid, { height: val }).catch(() => {});
    },
    [uid]
  );

  return {
    todayWeight,
    unit,
    height,
    weekData,
    logWeight,
    setUnit,
    setHeight,
  };
}
